/**
 * Workspace Master Management
 * DS-Wiki Knowledge Share Module
 */

import { useState, useEffect, useMemo } from 'react';
import { DataGrid } from '@/lib/dreamstep-sdk/datagrid';
import type { ColumnConfig, GridConfig } from '@/lib/dreamstep-sdk/datagrid';
import dsWikiService from '../../services/dsWikiService';
import type { Workspace } from '../../services/types';
import WorkspaceForm from './WorkspaceForm';
import { useToast } from '../../../../../hooks/useToast';
import ToastContainer from '../../../../../components/feature/ToastContainer';
import ConfirmDialog from '../../../../../components/base/ConfirmDialog';
import AccessDeniedDisplay from '../../../../../components/base/AccessDeniedDisplay';
import { PermissionModal } from '../../../../../components/masters/permission';
import { Breadcrumb } from '../../../../../components/base';
import { TableSkeleton } from '@/components/base/Skeleton';

export default function WorkspaceManagement() {
  const [workspaces, setWorkspaces] = useState<(Workspace & { sno: number })[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWorkspace, setEditingWorkspace] = useState<Workspace | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; workspace: Workspace | null }>({ isOpen: false, workspace: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const { toasts, removeToast, success, error: showError } = useToast();
  const [permissions] = useState<any>({ c_in: true, r_in: true, u_in: true, d_in: true });

  useEffect(() => { fetchWorkspaces(); }, []);

  const fetchWorkspaces = async () => {
    setLoading(true);
    try {
      const response = await dsWikiService.getAllWorkspaces();
      const data = Array.isArray(response.data) ? response.data : [];
      setWorkspaces(data.map((item, index) => ({ ...item, sno: index + 1 })));
    } catch (error: any) {
      showError(error.message || 'Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (workspace?: Workspace) => { setEditingWorkspace(workspace || null); setIsFormOpen(true); };
  const handleCloseForm = () => { setIsFormOpen(false); setEditingWorkspace(null); };
  const handleFormSuccess = () => { fetchWorkspaces(); };
  const handleDeleteClick = (workspace: Workspace) => { setDeleteDialog({ isOpen: true, workspace }); };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.workspace) return;
    setIsDeleting(true);
    try {
      await dsWikiService.deleteWorkspace(deleteDialog.workspace.dswiki_wrksp_id!);
      success('Workspace deleted successfully!');
      fetchWorkspaces();
      setDeleteDialog({ isOpen: false, workspace: null });
    } catch (error: any) {
      showError(error?.message || 'Failed to delete workspace');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnConfig[] = useMemo(() => [
    { field: 'sno', columnName: 'SNo', columnType: 'number', width: '80px', visible: true, filter: true, alignment: 'center',
      cellRenderer: (row: any) => <div className="font-mono text-gray-600 font-semibold">{row.sno}</div> },
    { field: 'wrksp_nm', columnName: 'Workspace Name', columnType: 'text', width: '250px', visible: true, filter: true,
      cellRenderer: (row: any) => (
        <div className="flex items-center gap-2">
          {row.icon_tx && <i className={`${row.icon_tx} text-lg`} style={{ color: row.clr_cd || '#6B7280' }}></i>}
          <span className="text-gray-900 font-medium">{row.wrksp_nm}</span>
          {row.is_dflt_in && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">Default</span>}
        </div>
      ) },
    { field: 'wrksp_cd', columnName: 'Code', columnType: 'text', width: '130px', visible: true, filter: true,
      cellRenderer: (row: any) => <span className="font-mono text-sm text-gray-700">{row.wrksp_cd}</span> },
    { field: 'dscn_tx', columnName: 'Description', columnType: 'text', width: '200px', visible: true, filter: true },
    { field: 'a_in', columnName: 'Active', columnType: 'text', width: '100px', visible: true, filter: true, alignment: 'center',
      cellRenderer: (row: any) => row.a_in ? <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs">Active</span> : <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs">Inactive</span> },
    { field: 'actions', columnName: 'Actions', columnType: 'button', width: '150px', visible: true, alignment: 'center', filter: false,
      cellRenderer: (row: any) => (
        <div className="flex items-center justify-center space-x-2" onClick={(e) => e.stopPropagation()}>
          <button onClick={(e) => { e.stopPropagation(); handleOpenForm(row); }} className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded" title="Edit"><i className="ri-edit-line text-lg"></i></button>
          <button onClick={(e) => { e.stopPropagation(); handleDeleteClick(row); }} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded" title="Delete"><i className="ri-delete-bin-line text-lg"></i></button>
        </div>
      ) }
  ], []);

  const gridConfig: GridConfig = useMemo(() => ({
    enableSearchPanel: true, enableColumnChooser: true, pagination: true, minPageSizes: [10, 25, 50], enableColumnResizing: true,
    headerCustomization: { style: 'modern', headerBackgroundColor: '#e0e7ff', headerTextColor: '#3730a3', showBorders: true },
    export: { enabled: true, formats: ['excel', 'pdf', 'csv'], title: 'Workspaces Master' },
    actionButton: { label: 'Add Workspace', icon: 'ri-add-line', onClick: () => handleOpenForm(), visible: true },
  }), []);

  return (
    <div className="h-full flex flex-col min-w-0 bg-gray-50">
      <div className="flex-shrink-0 module-header shadow-sm">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg"><i className="ri-layout-grid-line text-xl text-white"></i></div>
              <div>
                <Breadcrumb items={[{ label: 'DS-Wiki' }, { label: 'Masters' }]} currentPage="Workspaces" />
                <h1 className="text-lg font-semibold text-white">Workspaces</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-white/90 text-sm">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                <span><span className="font-semibold">{workspaces.length}</span> records</span>
              </div>
              <button onClick={() => setIsPermissionModalOpen(true)} className="p-1.5 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-colors" title="View Permissions"><i className="ri-shield-user-line"></i></button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden px-6 pb-6 pt-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm h-full overflow-hidden flex flex-col p-4">
          {loading ? (
            <TableSkeleton />
          ) : (permissions as any)?.accessDenied ? (
            <AccessDeniedDisplay message={(permissions as any).accessDeniedMessage} />
          ) : workspaces.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4"><i className="ri-layout-grid-line text-gray-400 text-4xl"></i></div>
              <p className="text-gray-600 font-medium text-lg">No workspaces found</p>
              <button onClick={() => handleOpenForm()} className="mt-6 flex items-center px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 shadow-md"><i className="ri-add-line mr-2"></i>Add First Workspace</button>
            </div>
          ) : (
            <DataGrid dataSource={workspaces} columns={columns} gridConfig={gridConfig} viewConfig={{ format: 'table' }} />
          )}
        </div>
      </div>

      <WorkspaceForm isOpen={isFormOpen} workspace={editingWorkspace} onClose={handleCloseForm} onSuccess={handleFormSuccess} />
      <ConfirmDialog isOpen={deleteDialog.isOpen} onClose={() => setDeleteDialog({ isOpen: false, workspace: null })} onConfirm={handleDeleteConfirm} title="Delete Workspace" message={`Are you sure you want to delete "${deleteDialog.workspace?.wrksp_nm}"?`} confirmText="Delete" cancelText="Cancel" type="danger" isLoading={isDeleting} />
      <PermissionModal isOpen={isPermissionModalOpen} onClose={() => setIsPermissionModalOpen(false)} permissions={permissions} entityName="Workspaces Master" />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
